import { ArrowRight, Check, Crown, Sparkles, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";

const REPORT_LANGUAGE_ORDER = ["id", "en"];

const ICONS = {
  free: null,
  starter: Sparkles,
  pro: Zap,
  business: Crown,
};

const PLAN_NAME_KEYS = {
  free: "pricing.plan.free",
  starter: "pricing.plan.starter",
  pro: "pricing.plan.pro",
  business: "pricing.plan.business",
};

const CARD_CLASS = {
  free: "border-border",
  starter: "border-emerald-500/30",
  pro: "border-brand-primary/40",
  business: "border-purple-500/45 shadow-[0_0_24px_rgba(147,51,234,0.18)]",
};
const ICON_CLASS = {
  free: "text-brand-primary",
  starter: "text-emerald-400",
  pro: "text-brand-primary",
  business: "text-purple-400",
};
const CTA_CLASS = {
  starter: "bg-brand-primary hover:bg-brand-primary/90 text-white",
  pro: "bg-brand-primary hover:bg-brand-primary/90 text-white",
  business:
    "bg-gradient-to-r from-purple-600 to-fuchsia-600 hover:from-purple-500 hover:to-fuchsia-500 text-white",
};

function getPlanLabel(plan, t) {
  const custom = String(plan?.name || "").trim();
  if (custom) return custom;
  const key = PLAN_NAME_KEYS[plan?.id];
  return key ? t(key) : String(plan?.id || "");
}

function getPriceLabel(plan, locale, t, billingCycle = "monthly") {
  const annualEnabled = Boolean(plan?.annual_enabled);
  const useAnnual = billingCycle === "annual" && annualEnabled;
  const monthlyPrice = Number(plan?.price_idr || 0);
  const annualMonthlyPrice = Number(plan?.annual_monthly_price_idr || 0);
  const displayPrice = useAnnual && annualMonthlyPrice > 0 ? annualMonthlyPrice : monthlyPrice;
  const annualDiscountAmount = Number(plan?.annual_discount_amount_idr || 0);

  if (displayPrice <= 0) {
    return {
      amount: t("pricing.free"),
      useAnnual: false,
      isFree: true,
      annualSave: null,
    };
  }

  return {
    amount: displayPrice.toLocaleString(locale),
    useAnnual,
    isFree: false,
    annualSave:
      useAnnual && annualDiscountAmount > 0
        ? t("pricing.billing.annualSave", {
            value: `Rp ${annualDiscountAmount.toLocaleString(locale)}`,
          })
        : null,
  };
}

function formatFileLimit(mb) {
  const value = Number(mb || 0);
  if (value <= 0) return "50 MB";
  if (value >= 1024 && value % 1024 === 0) return `${value / 1024} GB`;
  return `${value} MB`;
}

function getPlanFeatureMap(plan) {
  return plan?.feature_overrides && typeof plan.feature_overrides === "object"
    ? plan.feature_overrides
    : {};
}

function getReportLanguagesLabel(plan, t) {
  const featureMap = getPlanFeatureMap(plan);
  const raw = featureMap?.report_languages;
  const normalized = Array.isArray(raw)
    ? raw
        .map((lang) => String(lang || "").trim().toLowerCase())
        .filter((lang) => REPORT_LANGUAGE_ORDER.includes(lang))
    : [];
  const unique = Array.from(new Set(normalized));
  if (!unique.length) {
    return featureMap?.report_bilingual === false
      ? t("pricing.lang.id")
      : `${t("pricing.lang.id")}, ${t("pricing.lang.en")}`;
  }
  if (!unique.includes("id")) unique.unshift("id");
  const ordered = REPORT_LANGUAGE_ORDER.filter((lang) => unique.includes(lang));
  return ordered.map((lang) => t(`pricing.lang.${lang}`)).join(", ");
}

function getDetectionSummary(plan, t) {
  const media = plan?.media_access || {};
  const labels = [];
  if (media?.image) labels.push(t("pricing.feat.detect.image"));
  if (media?.video) labels.push(t("pricing.feat.detect.video"));
  if (media?.audio) labels.push(t("pricing.feat.detect.audio"));
  if (labels.length === 3) return t("pricing.feat.detect.all");
  return labels.join(", ");
}

function buildSummaryFeatures(plan, t) {
  const featureMap = getPlanFeatureMap(plan);
  const summary = [
    t("pricing.feat.quota.monthly", { count: Number(plan?.limit || 0) }),
    t("pricing.feat.quota.daily", { count: Number(plan?.daily_limit || 0) }),
    t("pricing.feat.quota.file", {
      size: formatFileLimit(plan?.file_limit_mb),
    }),
  ];

  const modelLabel = String(plan?.model_label || "").trim();
  if (modelLabel) summary.push(modelLabel);

  const detectionSummary = getDetectionSummary(plan, t);
  if (detectionSummary) summary.push(detectionSummary);

  const aiLevel = String(featureMap?.ai_explanation_level || "").toLowerCase();
  if (aiLevel === "full") summary.push(t("pricing.feat.report.aiFull"));
  else if (aiLevel === "basic") summary.push(t("pricing.feat.report.aiBasic"));

  summary.push(t("pricing.feat.report.languages", { value: getReportLanguagesLabel(plan, t) }));

  if (featureMap?.report_technical) {
    summary.push(t("pricing.feat.report.technical"));
  }
  if (featureMap?.report_pdf_download) {
    summary.push(t("pricing.feat.report.pdf"));
  }
  if (featureMap?.credits_topup_enabled) {
    summary.push(t("pricing.feat.storage.credits"));
  }
  if (featureMap?.support_api_access) {
    summary.push(t("pricing.feat.support.api"));
  }

  return Array.from(new Set(summary.map((item) => String(item || "").trim()).filter(Boolean))).slice(0, 7);
}

export default function PlanList({
  plans = [],
  currentPlan = "free",
  loading = false,
  processingPlanId = null,
  onPlanSelect,
  billingCycle = "monthly",
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-US" : "id-ID";

  const visiblePlans = [...plans]
    .filter((p) => p?.is_active !== false && p?.is_public !== false)
    .sort((a, b) => Number(a?.sort_order ?? 9999) - Number(b?.sort_order ?? 9999));

  const rankMap = visiblePlans.reduce((acc, plan, idx) => {
    acc[plan.id] = idx;
    return acc;
  }, {});
  const currentRank = rankMap[currentPlan] ?? -1;

  if (loading) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
        {t("common.loading")}
      </div>
    );
  }

  if (!visiblePlans.length) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
        {t("pricing.noPaidPlans")}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="app-plan-list">
      {visiblePlans.map((plan) => {
        const planId = plan?.id;
        const planLabel = getPlanLabel(plan, t);
        const Icon = ICONS[planId] || Sparkles;
        const isCurrent = planId === currentPlan;
        const isRecommended = Boolean(plan?.popular) && !isCurrent;
        const isDowngrade = !isCurrent && (rankMap[planId] ?? 0) < currentRank;
        const isBusy = processingPlanId === planId;
        const summaryFeatures = buildSummaryFeatures(plan, t);
        const priceLabel = getPriceLabel(plan, locale, t, billingCycle);
        const ctaClass = isDowngrade
          ? "bg-zinc-600 hover:bg-zinc-600/90 text-white"
          : CTA_CLASS[planId] || "bg-brand-primary hover:bg-brand-primary/90 text-white";

        const ctaLabel = isCurrent
          ? t("pricing.cta.current")
          : isDowngrade
            ? `${t("pricing.cta.downgrade")} ${planLabel}`
            : `${t("pricing.cta.upgrade")} ${planLabel}`;

        return (
          <div
            key={planId}
            className={`relative rounded-md border-2 bg-card p-4 flex flex-col ${
              CARD_CLASS[planId] || "border-border"
            } ${isRecommended ? "pt-8 shadow-[0_0_28px_rgba(34,211,238,0.2)]" : ""}`}
            data-testid={`app-plan-${planId}`}
          >
            {isRecommended && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="text-xs font-mono px-3 py-0.5 rounded-full bg-brand-primary text-white whitespace-nowrap">
                  {t("pricing.popular", { defaultValue: "Paling Populer" })}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {Icon && <Icon className={`w-4 h-4 ${ICON_CLASS[planId] || "text-brand-primary"}`} />}
                <h3 className="font-heading text-lg font-semibold">{planLabel}</h3>
              </div>
              <div className="flex items-center gap-1.5">
                {isCurrent && (
                  <span className="text-[10px] font-mono uppercase px-2 py-1 rounded-sm bg-brand-primary/10 text-brand-primary border border-brand-primary/20">
                    {t("pricing.currentPlanBadge", { defaultValue: "Plan Anda Saat Ini" })}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-2">
              {priceLabel.isFree ? (
                <p className="font-heading text-4xl font-bold tracking-tight leading-none">
                  {priceLabel.amount}
                </p>
              ) : (
                <div className="flex items-end gap-2">
                  <div className="font-heading text-4xl font-bold tracking-tight leading-none">
                    <span className="text-xl font-normal text-muted-foreground">Rp </span>
                    {priceLabel.amount}
                  </div>
                  <span className="text-sm text-muted-foreground pb-1">
                    /{t("pricing.perMonth")}
                  </span>
                </div>
              )}
              {priceLabel.useAnnual && (
                <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-brand-primary/10 text-brand-primary border border-brand-primary/20">
                    {t("pricing.billing.yearlyBilled", { defaultValue: "Ditagih tahunan" })}
                  </span>
                  {priceLabel.annualSave && (
                    <span className="text-xs text-muted-foreground">{priceLabel.annualSave}</span>
                  )}
                </div>
              )}
            </div>

            <div className="mt-3 flex-1 space-y-1.5">
              {summaryFeatures.map((feature) => (
                <div key={feature} className="flex items-start gap-1.5 text-sm text-muted-foreground">
                  <Check className="w-3.5 h-3.5 text-verdict-real mt-0.5 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <Button
              className={`mt-4 rounded-sm w-full gap-1.5 ${!isCurrent ? ctaClass : ""}`}
              variant={isCurrent ? "outline" : "default"}
              disabled={isCurrent || isBusy}
              onClick={() => onPlanSelect?.(plan)}
              data-testid={`app-plan-cta-${planId}`}
            >
              {!isCurrent && <ArrowRight className="w-3.5 h-3.5" />}
              {isBusy ? t("common.loading") : ctaLabel}
            </Button>
          </div>
        );
      })}
    </div>
  );
}
